/**
 * MainPlayer — single 16:9 video surface that shows the currently watched
 * camera. Crossfades 150ms when the watching identity changes.
 *
 * Reads selection from LayoutContext:
 *   - `selection.watchingIdentity` → which camera to render
 *   - `featuredFromRoom` → whether the watching one is the producer's pick
 *
 * Shows the FEATURED chip when watching matches the room's featured
 * identity. Shows LIVE CAMERA chip when the viewer has overridden.
 *
 * Falls through to EmptyState when there's no online camera to render.
 */

import { AnimatePresence, motion } from 'framer-motion'
import {
  registerPrimitive,
  type PrimitiveProps,
} from '../../layout'
import {
  CameraVideo,
  useCameraSelection,
  useCameras,
  useViewerCount,
} from '../../destination'
import { EmptyStateInner } from './EmptyState'

interface MainPlayerConfig {
  /** Render the FEATURED / LIVE CAMERA pill in the top-left. Default true. */
  showFeaturedChip?: boolean
  /** Render the bottom-left camera name overlay. Default true. */
  showName?: boolean
  /** Render the watching count pill in the top-right. Default true. */
  showWatchingCount?: boolean
}

export function PrimaryView({ config }: PrimitiveProps) {
  const cameras = useCameras()
  const selection = useCameraSelection()
  const viewerCount = useViewerCount()
  const cfg = config as MainPlayerConfig
  const showFeaturedChip = cfg.showFeaturedChip !== false
  const showName = cfg.showName !== false
  const showWatchingCount = cfg.showWatchingCount !== false

  const watchingIdentity = selection.selectedIdentity
  const camera = cameras.find((candidate) => candidate.identity === watchingIdentity)
  const isFeatured = Boolean(
    watchingIdentity && watchingIdentity === selection.producerFeaturedIdentity,
  )

  const liveCount = cameras.filter((c) => c.online).length
  const watching = viewerCount ?? 0

  return (
    <div className="lv-player">
      <AnimatePresence mode="sync" initial={false}>
        {camera ? (
          <motion.div
            key={camera.identity}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ position: 'absolute', inset: 0 }}
          >
            <CameraVideo
              identity={camera.identity}
              className="lv-player-feed"
              previewClassName="lv-player-feed"
              alt={camera.name}
              fallback={<EmptyStateInner state="awaiting-cameras" />}
            />
          </motion.div>
        ) : (
          <EmptyStateInner state={liveCount === 0 ? 'awaiting-cameras' : 'pre-event'} />
        )}
      </AnimatePresence>

      <div className="lv-player-vignette" />

      {showFeaturedChip && camera && (
        <span className="lv-player-live-chip" role="img" aria-label={isFeatured ? 'Featured camera' : 'Live camera'}>
          <span className="lv-live-dot" />
        </span>
      )}

      {showWatchingCount && watching > 0 && (
        <span className="lv-player-watching" role="img" aria-label={`${watching} watching live`}>
          <span className="lv-live-dot" />
          <b>{watching.toLocaleString()}</b>
        </span>
      )}

      {showName && camera && (
        <div className="lv-player-name-overlay">
          <h3 className="lv-player-name">{camera.name}</h3>
        </div>
      )}
    </div>
  )
}

registerPrimitive('PrimaryView', PrimaryView)
registerPrimitive('MainPlayer', PrimaryView)
